import { Button, ButtonMenu, Css, Icon, useModal } from "@homebound/beam";
import { useMemo } from "react";
import { TakeoffLineItemFilter } from "src/generated/graphqlTypes";
import { useCsvExportPost } from "src/hooks/useCsvExportPost";
import {
  useSelectedTliIds,
  useTakeoffsManagerContext,
  useTakeoffsStore,
} from "src/routes/libraries/plan-package/takeoffs/TakeoffsManagerContext";
import { BulkTaskAllocationModal } from "src/routes/libraries/plan-package/takeoffs/components/BulkTaskAllocationModal/BulkTaskAllocationModal";
import { DeleteItemsConfirmationModal } from "src/routes/libraries/plan-package/takeoffs/components/DeleteItemsConfirmationModal";
import { count, getDisabledReasons } from "src/utils";
import { AddEditLaborItemModal, AddEditMaterialItemModal, AddEditPlaceholderItemModal } from "./AddItems";
import { AddItemsFromExistingPlanModal } from "./AddItemsFromExistingPlanModal";
import { BulkEditModal } from "./BulkEditModal";
import { DuplicateItemsConfirmationModal } from "./DuplicateItemsConfirmationModal";

export function TakeoffsItemsTableBulkActionsMenu() {
  const { itemTableApi } = useTakeoffsStore((state) => state);
  const selectedTliIds = useSelectedTliIds(itemTableApi);

  return (
    <div css={Css.df.aic.gap1.$}>
      {/* Only show if multiple items are selected per spec */}
      {selectedTliIds.length > 1 && (
        <Button
          data-testid="clearSelections"
          variant="tertiary"
          label={`${count(selectedTliIds, "item")}  selected`}
          endAdornment={<Icon icon="x" />}
          onClick={() => itemTableApi && itemTableApi.clearSelections()}
        />
      )}
      <ActionsMenuButton selectedTliIds={selectedTliIds} />
    </div>
  );
}

function ActionsMenuButton({ selectedTliIds }: { selectedTliIds: string[] }) {
  const store = useTakeoffsManagerContext();
  const { planPackage, copyFromPlan, saveTli, filter } = store.getState();
  const { id: planPackageId, canEditLineItems } = planPackage;
  const { openModal } = useModal();
  const parameters = useMemo(
    () => ({
      filter: {
        ...(selectedTliIds.nonEmpty && { id: selectedTliIds }),
        ...filter,
      } satisfies TakeoffLineItemFilter,
      versionId: planPackage.version.id,
    }),
    [selectedTliIds, filter, planPackage.version.id],
  );
  const { handleDownload } = useCsvExportPost("takeoff", parameters);

  const items = useMemo(() => {
    return [
      {
        label: "Add Material",
        icon: "plus",
        onClick: () => {
          openModal({
            content: <AddEditMaterialItemModal takeoffsManagerStore={store} planPackageId={planPackageId} />,
          });
        },
        disabled: canEditLineItems.allowed === false ? getDisabledReasons(canEditLineItems) : false,
      },
      {
        label: "Add Placeholder",
        icon: "plus",
        onClick: () => {
          openModal({
            content: <AddEditPlaceholderItemModal takeoffsManagerStore={store} planPackageId={planPackageId} />,
          });
        },
        disabled: canEditLineItems.allowed === false ? getDisabledReasons(canEditLineItems) : false,
      },
      {
        label: "Add Labor",
        icon: "plus",
        onClick: () => {
          openModal({
            content: <AddEditLaborItemModal takeoffsManagerStore={store} planPackageId={planPackageId} />,
          });
        },
        disabled: canEditLineItems.allowed === false ? getDisabledReasons(canEditLineItems) : false,
      },
      {
        label: "Import items",
        icon: "upload",
        onClick: () => {
          openModal({
            content: (
              <AddItemsFromExistingPlanModal
                planPackageId={planPackageId}
                planPackageVersionId={planPackage.version.id}
                copyFromPlan={copyFromPlan}
                saveTli={saveTli}
              />
            ),
          });
        },
        disabled: canEditLineItems.allowed === false ? getDisabledReasons(canEditLineItems) : false,
      },
      {
        label: "Bulk Edit",
        icon: "pencil",
        onClick: () => openModal({ content: <BulkEditModal store={store} /> }),
        disabled:
          canEditLineItems.allowed === false
            ? getDisabledReasons(canEditLineItems)
            : selectedTliIds.isEmpty
              ? "Select items to delete"
              : false,
      },
      {
        label: "Delete",
        icon: "trash",
        onClick: () =>
          openModal({ content: <DeleteItemsConfirmationModal store={store} itemIdsToDelete={selectedTliIds} /> }),
        disabled:
          canEditLineItems.allowed === false
            ? getDisabledReasons(canEditLineItems)
            : selectedTliIds.isEmpty
              ? "Select items to delete"
              : false,
      },
      { label: "Download to .csv", icon: "download", onClick: handleDownload },
      {
        label: "Duplicate",
        icon: "duplicate",
        onClick: () => openModal({ content: <DuplicateItemsConfirmationModal store={store} /> }),
        disabled:
          canEditLineItems.allowed === false
            ? getDisabledReasons(canEditLineItems)
            : selectedTliIds.isEmpty
              ? "Select items to duplicate"
              : false,
      },
      {
        label: "Allocate Task",
        icon: "task",
        onClick: () => openModal({ content: <BulkTaskAllocationModal store={store} tliIdsToEdit={selectedTliIds} /> }),
        disabled:
          canEditLineItems.allowed === false
            ? getDisabledReasons(canEditLineItems)
            : selectedTliIds.isEmpty
              ? "No items selected"
              : false,
      },
    ];
  }, [canEditLineItems, selectedTliIds, handleDownload, openModal, store, planPackageId, copyFromPlan, saveTli]);

  return <ButtonMenu trigger={{ icon: "verticalDots" }} items={items} />;
}
